import type { DurationUnit } from "../components/CourseTab/types";

/** Course exam system — maps to backend enum when available. */
export type ExamSystem = "semester" | "year" | "na";

export const EXAM_SYSTEM_VALUES: ExamSystem[] = ["semester", "year", "na"];

/** Full year in months — used for N/A exam system term count (12 ÷ duration months, integer). */
export const MONTHS_PER_YEAR = 12;
/** Durations below 12 months use N/A exam system; 12 months and above use Annual / Semester. */
export const MAX_MONTHS_FOR_NA_EXAM_SYSTEM = 12;

export function normalizeExamSystem(value: unknown): ExamSystem | "" {
	if (value == null || value === "") return "";
	const lower = String(value).trim().toLowerCase();
	if (lower === "semester") return "semester";
	if (lower === "year" || lower === "annual") return "year";
	if (lower === "na" || lower === "n/a" || lower === "none") return "na";
	return "";
}

/** Convert course duration to approximate months (for exam/term rules). */
export function durationToMonths(duration: number | string, durationUnit: DurationUnit | string): number {
	const numericDuration = typeof duration === "string" ? Number(duration.trim()) : duration;
	if (!Number.isFinite(numericDuration) || numericDuration <= 0) return 0;
	switch (durationUnit) {
		case "year":
			return numericDuration * 12;
		case "month":
			return numericDuration;
		case "week":
			return (numericDuration * 7) / (365 / 12);
		case "day":
			return numericDuration / (365 / 12);
		default:
			return numericDuration;
	}
}

/** Convert course duration to whole days (minimum 1 when duration > 0). */
export function durationToDays(duration: number | string, durationUnit: DurationUnit | string): number {
	const numericDuration = typeof duration === "string" ? Number(duration.trim()) : duration;
	if (!Number.isFinite(numericDuration) || numericDuration <= 0) return 0;
	switch (durationUnit) {
		case "year":
			return numericDuration * 365;
		case "month":
			return Math.round(numericDuration * (365 / 12));
		case "week":
			return numericDuration * 7;
		case "day":
			return numericDuration;
		default:
			return numericDuration;
	}
}

/**
 * True when duration is under 12 months → Exam System must be N/A.
 * 12 months and longer use Semester / Annual (Year).
 */
export function isCourseDurationWithinOneYear(
	duration: number | string,
	durationUnit: DurationUnit | string,
): boolean {
	const months = durationToMonths(duration, durationUnit);
	return months > 0 && months < MAX_MONTHS_FOR_NA_EXAM_SYSTEM;
}

export function normalizeExamSystemName(name: string): string {
	return name.trim().toLowerCase();
}

export function isNaExamSystemName(name: string): boolean {
	const normalized = normalizeExamSystemName(name);
	return normalized === "na" || normalized === "n/a" || normalized === "none";
}

export function isSemesterExamSystemName(name: string): boolean {
	const normalized = normalizeExamSystemName(name);
	return normalized === "semester" || normalized.includes("semester");
}

/** Annual exam system — API may label it "Year" or "Annual". */
export function isAnnualOrYearExamSystemName(name: string): boolean {
	const normalized = normalizeExamSystemName(name);
	return (
		normalized === "year" ||
		normalized === "annual" ||
		normalized === "yearly" ||
		normalized.includes("annual")
	);
}

export function isYearOrSemesterExamSystemName(name: string): boolean {
	return isSemesterExamSystemName(name) || isAnnualOrYearExamSystemName(name);
}

/**
 * Term count for courses under 12 months (N/A exam system):
 * integer division — 12 ÷ months (no floating point).
 * e.g. 3→4, 4→3, 6→2, 7–11→1
 */
export function calculateNaExamSystemTermCount(durationMonths: number): number | null {
	if (!Number.isFinite(durationMonths) || durationMonths <= 0) return null;
	if (durationMonths >= MAX_MONTHS_FOR_NA_EXAM_SYSTEM) return null;
	const terms = Math.floor(MONTHS_PER_YEAR / durationMonths);
	return terms > 0 ? terms : null;
}

/**
 * Term count rules:
 * - Duration < 12 months (Exam System N/A): terms = floor(12 / months)
 *   e.g. 3→4, 4→3, 6→2, 7–11→1
 * - Duration ≥ 12 months + Semester: 2
 * - Duration ≥ 12 months + Annual/Year: 1
 */
export function calculateTermCount(
	duration: number | string,
	durationUnit: DurationUnit | string,
	examSystemName?: string | null,
): number | null {
	const months = durationToMonths(duration, durationUnit);
	if (months <= 0) return null;

	if (months < MAX_MONTHS_FOR_NA_EXAM_SYSTEM) {
		return calculateNaExamSystemTermCount(months);
	}

	if (!examSystemName?.trim()) return null;
	if (isSemesterExamSystemName(examSystemName)) return 2;
	if (isAnnualOrYearExamSystemName(examSystemName)) return 1;
	return null;
}

/** Resolve exam system display name from id using the options list (id or label fallback). */
export function resolveExamSystemNameById(
	options: Array<{ id: number; name: string }>,
	id: string | number | null | undefined,
): string | null {
	const selected = findExamSystemOptionById(options, id);
	return selected?.name?.trim() ? selected.name.trim() : null;
}

export function findExamSystemOptionById(
	options: Array<{ id: number; name: string }>,
	id: string | number | null | undefined,
): { id: number; name: string } | undefined {
	if (id == null || id === "") return undefined;
	return options.find(option => String(option.id) === String(id));
}

export function findNaExamSystemOption(
	options: Array<{ id: number; name: string }>,
): { id: number; name: string } | undefined {
	return options.find(option => isNaExamSystemName(option.name));
}

export function requiresPromotionStages(
	examSystemId: string | undefined | null,
	duration: string | undefined | null,
	durationUnit: string | undefined | null,
	options: Array<{ id: number; name: string }>,
): boolean {
	if (!examSystemId?.trim() || !duration?.trim() || !durationUnit?.trim()) return false;
	if (isCourseDurationWithinOneYear(duration, durationUnit)) return false;
	const selected = findExamSystemOptionById(options, examSystemId);
	return selected != null && isYearOrSemesterExamSystemName(selected.name);
}

/**
 * Promotion stage count for Year/Semester exam systems (duration ≥ 12 months):
 * - Semester: floor(months / 6) — one stage per semester
 * - Annual/Year: floor(months / 12) — one stage per year
 */
export function calculatePromotionStages(
	duration: number | string,
	durationUnit: DurationUnit | string,
	examSystemName?: string | null,
): number | null {
	const months = durationToMonths(duration, durationUnit);
	if (months < MAX_MONTHS_FOR_NA_EXAM_SYSTEM) return null;
	if (!examSystemName?.trim()) return null;
	if (isSemesterExamSystemName(examSystemName)) {
		const stages = Math.floor(months / 6);
		return stages > 0 ? stages : null;
	}
	if (isAnnualOrYearExamSystemName(examSystemName)) {
		const stages = Math.floor(months / 12);
		return stages > 0 ? stages : null;
	}
	return null;
}

/** Resolve promotion stages string for POST/PATCH payload (e.g. "6"). */
export function resolvePromotionStagesForPayload(promotionStages: string | undefined | null): number | null {
	if (promotionStages == null || promotionStages === "" || promotionStages === "—") return null;
	const trimmed = promotionStages.trim();
	if (!trimmed) return null;
	const value = Number(trimmed);
	return Number.isFinite(value) && value > 0 ? value : null;
}

/** Resolve promotion stages for API — uses stored value or derives from duration + exam system. */
export function resolvePromotionStagesForCoursePayload(
	courseDetails: {
		promotionStages?: string | null;
		duration?: string | null;
		durationUnit?: string | null;
		examSystemId?: string | null;
	},
	examSystemOptions: Array<{ id: number; name: string }>,
): number | null {
	const stored = resolvePromotionStagesForPayload(courseDetails.promotionStages);
	if (stored != null) return stored;

	if (
		!requiresPromotionStages(
			courseDetails.examSystemId,
			courseDetails.duration,
			courseDetails.durationUnit,
			examSystemOptions,
		)
	) {
		return null;
	}

	const examSystemName = resolveExamSystemNameById(examSystemOptions, courseDetails.examSystemId);
	return calculatePromotionStages(
		courseDetails.duration ?? "",
		courseDetails.durationUnit ?? "",
		examSystemName,
	);
}

export function resolveExamSystemIdForPayload(selectedValue: string | undefined | null): number | null {
	if (!selectedValue?.trim()) return null;
	const id = Number(selectedValue);
	return Number.isFinite(id) && id > 0 ? id : null;
}

/** Resolve term count string for POST/PATCH payload (e.g. "4"). */
export function resolveTermCountForPayload(termCount: string | undefined | null): string | null {
	if (termCount == null || termCount === "" || termCount === "—") return null;
	const trimmed = termCount.trim();
	return trimmed || null;
}

/** Resolve term count for API — uses stored value or derives from duration + exam system. */
export function resolveTermCountForCoursePayload(
	courseDetails: {
		termCount?: string | null;
		duration?: string | null;
		durationUnit?: string | null;
		examSystemId?: string | null;
	},
	examSystemOptions: Array<{ id: number; name: string }>,
): string | null {
	const stored = resolveTermCountForPayload(courseDetails.termCount);
	if (stored != null) return stored;

	const examSystemName = resolveExamSystemNameById(examSystemOptions, courseDetails.examSystemId);
	const terms = calculateTermCount(
		courseDetails.duration ?? "",
		courseDetails.durationUnit ?? "",
		examSystemName,
	);
	return terms != null ? String(terms) : null;
}

/** Resolve exam system display name for POST/PATCH payload (e.g. "Semester"). */
export function resolveExamSystemNameForPayload(
	selectedValue: string | undefined | null,
	options: Array<{ id: number; name: string }>,
): string | null {
	if (!selectedValue?.trim()) return null;
	const trimmed = selectedValue.trim();
	const byId = options.find(o => String(o.id) === trimmed);
	if (byId) return byId.name;
	const byName = options.find(o => o.name.toLowerCase() === trimmed.toLowerCase());
	if (byName) return byName.name;
	if (Number.isNaN(Number(trimmed))) return trimmed;
	return null;
}

/** Human-readable exam system for course details view (API may return string or nested object). */
export function resolveExamSystemDisplayValue(examSystem: unknown): string | null {
	if (examSystem == null) return null;
	if (typeof examSystem === "object") {
		const name = (examSystem as { name?: string }).name;
		return name?.trim() ? name.trim() : null;
	}
	if (typeof examSystem === "string" && examSystem.trim()) return examSystem.trim();
	return null;
}

/** Human-readable term count for course details view (API may return string or number). */
export function resolveTermCountDisplayValue(termCount: unknown): string | null {
	if (termCount == null || termCount === "") return null;
	const value = String(termCount).trim();
	return value || null;
}

/** Resolve exam system form value from API list/detail shapes (id preferred for select). */
export function resolveExamSystemFormValue(
	examSystem: unknown,
	examSystemId?: number | null,
): string {
	if (examSystem && typeof examSystem === "object") {
		const obj = examSystem as { id?: number; uuid?: string; name?: string };
		if (obj.id != null) return String(obj.id);
		if (obj.uuid) return obj.uuid;
	}
	if (examSystemId != null) return String(examSystemId);
	if (typeof examSystem === "string" && examSystem.trim()) return examSystem.trim();
	return "";
}

/** Map API exam system name to select option id when hydrating edit form. */
export function resolveExamSystemSelectValue(
	examSystem: unknown,
	examSystemId: number | null | undefined,
	options: Array<{ id: number; name: string }>,
): string {
	const raw = resolveExamSystemFormValue(examSystem, examSystemId);
	if (!raw) return "";
	const byId = options.find(o => String(o.id) === raw);
	if (byId) return String(byId.id);
	const byName = options.find(o => o.name.toLowerCase() === raw.toLowerCase());
	if (byName) return String(byName.id);
	return raw;
}
